import { type ReactNode, useMemo } from 'react';

import { Stack } from '@mui/material';

import { gql } from '@graphql/__generated__';
import type { LotCardCarousel_LotFragment } from '@graphql/__generated__/graphql';
import { CardCarousel } from '@modules/_shared/components/CardCarousel';
import { Tag } from '@modules/_shared/components/Tag';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  lot: LotCardCarousel_LotFragment;
  // Extra badges stacked under the online/offline status, over the image.
  statusExtra?: ReactNode;
};

export const LOT_CARD_CAROUSEL_FRAGMENT = gql(/* GraphQL */ `
  fragment LotCardCarousel_lot on lots {
    lot_reference
    created_at
    created_by_user {
      id
      first_name
      last_name
    }
    property {
      id
      property_images(order_by: { order_nr: asc }) {
        id
        title
        image {
          id
          url
        }
      }
    }
    portal_listings {
      id
      live
    }
  }
`);

const LivePortalListingsCount = ({
  portal_listings,
}: {
  portal_listings: LotCardCarousel_LotFragment['portal_listings'];
}) => {
  const { t } = useLocale();

  const livePortalListingsCount =
    portal_listings?.filter(x => x.live).length ?? 0;

  return (
    <Tag
      status={livePortalListingsCount > 0 ? 'dark_success' : 'dark_error'}
      noBorder
    >
      {livePortalListingsCount > 0 ? t('online') : t('offline')}
      {livePortalListingsCount > 0 && (
        <span css={{ marginLeft: 6, opacity: '.8' }}>
          {livePortalListingsCount}
        </span>
      )}
    </Tag>
  );
};

export const LotCardCarousel = (props: Props) => {
  const { lot, statusExtra } = props;

  const images = useMemo(
    () =>
      (lot.property?.property_images ?? []).reduce<
        { id: string; url: string }[]
      >((acc, { image }) => {
        if (image.url != null) {
          acc.push({ id: image.id, url: image.url });
        }

        return acc;
      }, []),
    [lot.property?.property_images],
  );

  const firstName = lot.created_by_user?.first_name;
  const lastName = lot.created_by_user?.last_name;
  const createdBy =
    firstName != null && lastName != null
      ? `${firstName.charAt(0)}. ${lastName}`
      : null;

  return (
    <CardCarousel
      images={images}
      topOverlayContent={
        <>
          {lot.created_at != null && createdBy != null ? (
            <Tag
              status="dark"
              noBorder
              sx={{ minWidth: 0, alignSelf: 'flex-start' }}
            >
              <TimeAgo dateString={lot.created_at} /> - {createdBy}
            </Tag>
          ) : (
            <div></div>
          )}
          <Stack sx={{ gap: 1, alignItems: 'flex-end', flexShrink: 0 }}>
            <LivePortalListingsCount portal_listings={lot.portal_listings} />
            {statusExtra}
          </Stack>
        </>
      }
      bottomOverlayContent={
        <Tag status="neutral" noBorder>
          {lot.lot_reference}
        </Tag>
      }
    />
  );
};
